import 'slick-carousel/slick/slick.css'
import 'slick-carousel/slick/slick-theme.css'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import cls from './widget-swiper.module.scss'
import Slider from 'react-slick'
import classNames from 'classnames'
import PropTypes from 'prop-types'
import MaterialIcon from '../MaterialIcon/MaterialIcon'

const AUTO_BREAK_TIME = 3000

export const WidgetSwiper = props => {
  const {
    className,
    classNameContainer,
    children,
    pagination,
    arrows,
    isAutoPlay,
    slidesToShow,
    slidesToScroll,
    speed,
    variableWidth,
    infinite = true,
  } = props
  const sliderRef = useRef(null)
  const [active, setActive] = useState(0)

  const goNext = useCallback(e => {
    e.preventDefault()
    e.stopPropagation()
    return sliderRef.current?.slickNext()
  }, [])
  const goPrev = useCallback(e => {
    e.preventDefault()
    e.stopPropagation()
    return sliderRef.current?.slickPrev()
  }, [])
  const goTo = useCallback(
    i => {
      return sliderRef.current?.slickGoTo(i * slidesToScroll)
    },
    [slidesToScroll],
  )

  const processedSettings = useMemo(() => {
    return {
      variableWidth,
      className: classNames(cls.slider, className),
      arrows: !!arrows,
      dots: !!pagination,
      appendDots: dots => (
        <WidgetSwiperPagination
          variant={pagination.variant}
          classNameContainer={pagination.classNameContainer}
          classNameDotsContainer={pagination.classNameDotsContainer}
          classNameDot={pagination.classNameDot}
          withArrows={arrows?.variant === 'bottom'}
          dots={dots}
          onGoTo={goTo}
          onPrev={goPrev}
          onNext={goNext}
          active={active}
          setActive={setActive}
        />
      ),
      speed,
      slidesToShow,
      slidesToScroll,
      afterChange: idx => setActive(idx / slidesToScroll),
      infinite,
      ...(arrows?.variant === 'side'
        ? { prevArrow: <WidgetSwiperButton type="prev" customClassName={arrows.classNamePrev} /> }
        : {}),
      ...(arrows?.variant === 'side'
        ? { nextArrow: <WidgetSwiperButton type="next" customClassName={arrows.classNameNext} /> }
        : {}),
      ...(isAutoPlay
        ? {
            autoplay: true,
            autoplaySpeed: AUTO_BREAK_TIME,
            pauseOnHover: true,
            pauseOnFocus: false,
          }
        : {}),
    }
  }, [
    variableWidth,
    arrows,
    pagination,
    isAutoPlay,
    className,
    slidesToShow,
    slidesToScroll,
    speed,
    active,
    goNext,
    goPrev,
    goTo,
    infinite,
  ])

  useEffect(() => {
    setActive(0)
  }, [pagination])

  if (!children) return null

  return (
    <div className={classNames(cls.container, classNameContainer)}>
      <Slider ref={sliderRef} {...processedSettings}>
        {children}
      </Slider>
    </div>
  )
}

const WidgetSwiperButton = props => {
  const { style, onClick, className, customClassName, type = 'prev' } = props

  const handleClick = useCallback(
    e => {
      e.stopPropagation()
      onClick(e)
    },
    [onClick],
  )

  return (
    <div
      className={classNames(cls.button, className, customClassName)}
      style={style}
      onClick={handleClick}
    >
      <MaterialIcon
        name={type === 'prev' ? 'arrow_back_ios_new' : 'arrow_forward_ios'}
        ariaLabel={type === 'prev' ? 'Prev' : 'Next'}
        size={22}
        isSymbol
      />
    </div>
  )
}

const WidgetSwiperPagination = props => {
  const {
    withArrows = true,
    classNameContainer,
    classNameDotsContainer,
    classNameDot,
    dots,
    onGoTo,
    onPrev,
    onNext,
    variant,
    active,
    setActive,
  } = props

  const handleClick = useCallback(
    (e, index) => {
      e.preventDefault()
      e.stopPropagation()
      onGoTo(index)
      setActive(index)
    },
    [onGoTo, setActive],
  )

  return (
    <div className={classNameContainer}>
      {withArrows && <WidgetSwiperButton type="prev" onClick={onPrev} />}
      <div className={classNameDotsContainer}>
        {variant === 'numeric' ? (
          <div className={cls.pages}>{`${active + 1}/${dots.length}`}</div>
        ) : (
          dots.map((_, index) => (
            <button
              key={index}
              onClick={e => handleClick(e, index)}
              className={classNames(cls.dot, { [cls.dotActive]: index === active }, classNameDot)}
            />
          ))
        )}
      </div>
      {withArrows && <WidgetSwiperButton type="next" onClick={onNext} />}
    </div>
  )
}

WidgetSwiper.propTypes = {
  className: PropTypes.string,
  classNameContainer: PropTypes.string,
  children: PropTypes.shape({}),
  pagination: PropTypes.shape({
    variant: PropTypes.oneOf(['numeric', 'round']),
    classNameContainer: PropTypes.string,
    classNameDotsContainer: PropTypes.string,
    classNameDot: PropTypes.string,
  }),
  arrows: PropTypes.shape({
    variant: PropTypes.oneOf(['side', 'bottom']),
    classNamePrev: PropTypes.string,
    classNameNext: PropTypes.string,
  }),
  isAutoPlay: PropTypes.bool,
  variableWidth: PropTypes.bool,
  slidesToShow: PropTypes.number,
  slidesToScroll: PropTypes.number,
  speed: PropTypes.number,
}

WidgetSwiper.defaultProps = {
  isAutoPlay: false,
  variableWidth: false,
  slidesToShow: 1,
  slidesToScroll: 1,
  speed: 300,
}
